import { Button, Flex, Modal, theme, Typography } from 'antd';
import { type SortOrder } from 'antd/es/table/interface';
import { CircleAlert, PlusCircle, Trash2 } from 'lucide-react';
import React, { type Key, type ReactNode, useEffect, useReducer, useState } from 'react';
import { catchError, EMPTY, from, of, switchMap, tap, throwError } from 'rxjs';
import IngredientForm from './ingredient.form';
import IngredientsFilter, { type IngredientsFiltersProps } from './ingredients.filter';
import AuthorizedComponent from '../../../components/authorized-component';
import { DataTable, type DataTableProps } from '../../../components/data-table';
import SyncSuspense from '../../../components/sync-suspense';
import { ExtraContent } from '../../../contexts/extra-content-context-provider';
import { Ingredient } from '../../../entities';
import useDebouncedState from '../../../hooks/use-debounced-state';
import { useNotification } from '../../../hooks/use-notification';
import { useQuery } from '../../../jsonapi-provider';
import { Roles } from '../../../types';

const DEFAULT_PAGE_SIZE = 10;
const DEFAULT_PAGE_NUMBER = 1;
const DEFAULT_SORT_FIELD = 'name';
const DEFAULT_SORT_DIRECTION: SortOrder = 'ascend';
const SUCCESS_DELETE_MESSAGE = 'Ingredient deleted successfully';

interface IngredientsPageState {
  filters?: IngredientsFiltersProps['initialFilters'];
  pagination: { current: number; pageSize: number };
  sort: { order?: SortOrder; field?: Key | readonly Key[] };
}

const serializeSort = (sort: IngredientsPageState['sort']): string | undefined => {
  if (sort.field && sort.order) {
    const field = Array.isArray(sort.field) ? sort.field.join(',') : sort.field.toString();
    return `${sort.order === 'descend' ? '-' : ''}${field}`;
  }
  return undefined;
};

const serializeFilters = (filters: IngredientsPageState['filters']): string | undefined => {
  const parts: string[] = [];
  if (filters?.query) parts.push(`name=ini='*${filters.query}*'`);
  if (filters?.foodGroup && filters.foodGroup.length > 0)
    parts.push(`foodGroup.id=in=(${filters.foodGroup.map((fg) => `${fg}`).join(',')})`);

  return parts.length ? parts.join(';') : undefined;
};

const reducer = (
  state: IngredientsPageState,
  action: {
    type: 'RESET_FILTER' | 'SET_FILTER' | 'SET_PAGINATION' | 'SET_SORT';
    payload?:
      | IngredientsPageState['filters']
      | IngredientsPageState['pagination']
      | IngredientsPageState['sort'];
  }
): IngredientsPageState => {
  switch (action.type) {
    case 'RESET_FILTER':
      return { ...state, filters: {} };
    case 'SET_FILTER':
      return {
        ...state,
        filters: action.payload as IngredientsPageState['filters'],
        pagination: {
          ...state?.pagination,
          current: DEFAULT_PAGE_NUMBER,
        },
      };
    case 'SET_PAGINATION':
      return {
        ...state,
        pagination: action.payload as IngredientsPageState['pagination'],
      };
    case 'SET_SORT':
      return {
        ...state,
        sort: action.payload as IngredientsPageState['sort'],
      };
  }
};

const IngredientsPage = (): ReactNode => {
  const notification = useNotification();
  const { token } = theme.useToken();
  const [state, setState] = useReducer(reducer, {
    filters: {},
    pagination: { current: DEFAULT_PAGE_NUMBER, pageSize: DEFAULT_PAGE_SIZE },
    sort: { field: DEFAULT_SORT_FIELD, order: DEFAULT_SORT_DIRECTION },
  });
  const [debouncedState] = useDebouncedState({ state, time: 1000 });
  const [ingredientToEdit, setIngredientToEdit] = useState<Ingredient>();
  const ingredients = useQuery<Ingredient[]>(
    debouncedState && [
      'ingredient',
      {
        include: ['foodGroup'],
        filter: serializeFilters(debouncedState.filters),
        page: {
          size: debouncedState.pagination.pageSize,
          number: debouncedState.pagination.current,
          totals: 1,
        },
        sort: serializeSort(debouncedState.sort),
      },
    ]
  );

  useEffect(() => {
    ingredients.error &&
      notification.error({
        message: ' ',
        description: ingredients.error.detail,
      });
  }, [ingredients.error]);

  const columns: DataTableProps<Ingredient>['columns'] = [
    {
      title: 'Name',
      dataIndex: 'name',
      sorter: true,
      sortDirections: ['ascend', 'descend', 'ascend'],
      defaultSortOrder: DEFAULT_SORT_DIRECTION,
      render: (value, item) => (
        <Typography.Link
          onClick={() => {
            setIngredientToEdit(item);
          }}
        >
          {value}
        </Typography.Link>
      ),
    },
    {
      title: 'Food group',
      dataIndex: 'foodGroup',
      render: (value: Ingredient['foodGroup']) => value.name,
      width: '18%',
    },
    // {
    //   title: 'Diets',
    //   dataIndex: 'diets',
    //   render: (value: Ingredient['diets']) => (
    //     <Space size="small" wrap>
    //       {!value?.length && '-'}
    //       {value.map((diet) => (
    //         <Tag key={diet} color="blue">
    //           {diet}
    //         </Tag>
    //       ))}
    //     </Space>
    //   ),
    // },
    {
      title: 'Description',
      dataIndex: 'description',
      ellipsis: true,
      render: (value) => (!value?.length ? '-' : value),
    },
  ];

  return (
    <Flex gap="middle" vertical>
      <DataTable
        title="Ingredients"
        description="List of ingredients"
        filtersNode={
          <IngredientsFilter
            initialFilters={state.filters}
            onChange={(filters) => {
              setState({ type: 'SET_FILTER', payload: filters });
            }}
          />
        }
        actionsNode={
          <AuthorizedComponent roles={[Roles.ROLE_HHC_ADMIN]}>
            <Button
              type="primary"
              icon={<PlusCircle />}
              onClick={() => {
                setIngredientToEdit(new Ingredient());
              }}
            >
              Add Ingredient
            </Button>
          </AuthorizedComponent>
        }
        dataList={ingredients}
        currentPage={state.pagination.current}
        onChange={setState}
        columns={columns}
        actions={[
          {
            key: 'delete',
            label: 'Delete',
            icon: <Trash2 />,
            danger: true,
            onItemClick: (record) => {
              Modal.confirm({
                width: '50%',
                title: 'Confirm deletion',
                icon: <CircleAlert />,
                content: (
                  <Typography.Text>
                    Are you sure you want to delete <b>{record.name}</b> permanently?
                  </Typography.Text>
                ),
                okText: 'Yes',
                okType: 'danger',
                cancelText: 'No',
                onOk: () => {
                  from(ingredients.client.delete(['ingredient', record.id]))
                    .pipe(
                      switchMap((res) =>
                        res.error
                          ? throwError(() => new Error(res.error?.detail ?? res.error?.title))
                          : of(res.data)
                      ),
                      tap(() => {
                        notification.success({ message: ' ', description: SUCCESS_DELETE_MESSAGE });
                        ingredients.refetch?.();
                      }),
                      catchError((error: Error) => {
                        notification.error({
                          message: 'Failed to delete ingredient',
                          description: error.message,
                        });
                        return EMPTY;
                      })
                    )
                    .subscribe();
                },
              });
            },
          },
        ]}
      />

      <SyncSuspense condition={ingredientToEdit}>
        <ExtraContent>
          <IngredientForm
            ingredient={ingredientToEdit!}
            onSuccess={(_ingredient, newIngredient) => {
              ingredients.refetch?.();
              if (newIngredient) setIngredientToEdit(new Ingredient());
              else setIngredientToEdit(undefined);
            }}
            onCancel={() => {
              setIngredientToEdit(undefined);
            }}
          />
        </ExtraContent>
      </SyncSuspense>
    </Flex>
  );
};

export default IngredientsPage;
